/**
 * Where the server is, and how this build proves who it is.
 *
 * There are two builds of this client out of one source tree. The web one is
 * served by the server it talks to: URLs are relative, the session is a cookie
 * the browser attaches on its own, and none of the machinery below does
 * anything. The native one is bundled inside a Capacitor shell and its page
 * comes from `capacitor://localhost`, which is a different origin from the
 * server by construction — so it needs absolute URLs, and a credential it can
 * send itself, because a cross-site cookie is not one.
 *
 * `VITE_API_ORIGIN` is the whole switch: unset in the web build, in dev and in
 * every test, so those paths are byte-for-byte what they always were.
 *
 * This module imports nothing, deliberately. errorReporter.ts is the one place
 * in the app that may not go through api/client.ts — a request whose failure
 * would be reported by the thing that failed — and it still needs all of this.
 */

/** The server's origin on a native build; empty string on the web. */
export const apiOrigin: string = import.meta.env.VITE_API_ORIGIN ?? '';

/** Whether this bundle is the one inside a native shell. */
export const isNative: boolean = apiOrigin !== '';

/**
 * Which shell this is — `ios`, `android`, or `web` off the phone.
 *
 * `isNative` above is a build flag and answers «есть ли вокруг оболочка». This
 * answers «какая», which stopped being a decoration once there were two of them:
 * it picks the push transport to register with, and it names the device's
 * directory on the server (`deviceSegment` in server/src/gallery.ts).
 *
 * Read off the global Capacitor injects into the page rather than imported from
 * @capacitor/core, for the reason `galleryPlugin()` documents at length: that
 * import never settles inside the shell, and this module imports nothing anyway.
 *
 * A shell too old to answer gets `unknown` rather than a guess. The guess would
 * have to be 'ios', which is what both call sites assumed while iOS was the only
 * shell there was — and an Android phone quietly filed as an iPhone is the one
 * wrong answer that looks exactly like a right one.
 */
export function nativePlatform(): string {
  if (!isNative) return 'web';
  const cap = (globalThis as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  return cap?.getPlatform?.() ?? 'unknown';
}

const TOKEN_KEY = 'chat:session';
const TICKET_KEY = 'chat:media-ticket';

/**
 * localStorage rather than memory, and that is right here in a way it would not
 * be on the web. A WKWebView's storage is application data, not the
 * script-writable web storage Safari evicts after seven days, so this is what
 * makes a cold start still be signed in. On the web nothing is ever written
 * here: the session stays an httpOnly cookie no script can read.
 */
function read(key: string): string | null {
  if (!isNative) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  if (!isNative) return;
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage full or unavailable. The session survives in memory for as long
    // as the app is running, which is the ordinary case; only the cold start
    // after this is lost, and it lands on the login screen rather than nowhere.
  }
}

export function sessionToken(): string | null {
  return read(TOKEN_KEY);
}

export function setSessionToken(token: string | null): void {
  write(TOKEN_KEY, token);
}

export function mediaTicket(): string | null {
  return read(TICKET_KEY);
}

export function setMediaTicket(ticket: string | null): void {
  write(TICKET_KEY, ticket);
}

/** Both credentials at once — what signing out has to clear. */
export function clearCredentials(): void {
  setSessionToken(null);
  setMediaTicket(null);
}

/** An API path — `/auth/me` — as the URL to actually fetch. */
export function apiUrl(path: string): string {
  return `${apiOrigin}/api${path}`;
}

/**
 * The live connection's address.
 *
 * The token goes in the query because it has nowhere else to go: a WebSocket
 * opened from JavaScript cannot carry custom headers, so the bearer arrangement
 * the rest of the API uses is unavailable here. On the web this is unchanged —
 * the cookie authenticates the upgrade, and there is no token to pass.
 */
export function wsUrl(): string {
  if (!isNative) {
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${location.host}/ws`;
  }
  const base = `${apiOrigin.replace(/^http/, 'ws')}/ws`;
  const token = sessionToken();
  return token === null ? base : `${base}?token=${encodeURIComponent(token)}`;
}

/** The bearer header on a native build; nothing at all on the web. */
export function authHeaders(): Record<string, string> {
  const token = sessionToken();
  return token === null ? {} : { Authorization: `Bearer ${token}` };
}

/**
 * `omit` on native: there is no cookie worth sending cross-origin, the server
 * does not allow credentials from that origin anyway (see server/src/cors.ts),
 * and asking for them would only turn every response into a CORS failure.
 */
export function credentialsMode(): RequestCredentials {
  return isNative ? 'omit' : 'include';
}

/**
 * The media ticket as a query parameter, ready to append — empty on the web.
 *
 * `<img>` and `<video>` cannot send an Authorization header, which is the whole
 * reason this credential exists separately from the bearer token. It is stable
 * for a day at a time on the server's side, and that matters here: attachments
 * are served `immutable`, so a ticket that changed per render would change every
 * URL and re-download every picture.
 */
export function mediaQuery(): string {
  const ticket = mediaTicket();
  return ticket === null ? '' : `t=${encodeURIComponent(ticket)}`;
}
